import React from 'react';
import { styled } from '@material-ui/core';
import { SidebarItem } from '@backstage/core-components';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import ErrorIcon from '@material-ui/icons/Error';
import {
  useRequestNewCount,
  useUserRequestsPermissions,
} from '../userRequests/hooks';
import { RequestCounterKey } from '../userRequests/types';

const UserRequestsItem = styled(SidebarItem)({
  '& .MuiTypography-root': {
    overflow: 'visible',
    textOverflow: 'clip',
  },
});

const UserRequestsLabel = styled('span')({
  display: 'inline-flex',
  alignItems: 'baseline',
  whiteSpace: 'nowrap',
  '& sup': {
    marginLeft: 4,
    lineHeight: 0,
  },
});

const NewRequestsIcon = styled(ErrorIcon)({
  width: 14,
  height: 14,
  color: '#fff',
});

type SectionPermission = { read?: boolean; write?: boolean };

const COUNTED_SECTIONS = [
  { counterKey: RequestCounterKey.SING_UP, permissionKey: 'registration' },
  { counterKey: RequestCounterKey.SUPPORT, permissionKey: 'support' },
  { counterKey: RequestCounterKey.INTAKE, permissionKey: 'intake' },
] as const;

function canViewSection(section?: SectionPermission) {
  return Boolean(section?.read || section?.write);
}

function hasVisibleNewRequests(
  permissions: Record<
    (typeof COUNTED_SECTIONS)[number]['permissionKey'],
    SectionPermission
  >,
  counters: Partial<Record<RequestCounterKey, number>> | undefined,
) {
  return COUNTED_SECTIONS.some(({ counterKey, permissionKey }) => {
    if (!canViewSection(permissions[permissionKey])) {
      return false;
    }

    return (counters?.[counterKey] ?? 0) > 0;
  });
}

export const UserRequestsSidebarItem = () => {
  const { data: counters } = useRequestNewCount();
  const { permissions, isLoading } = useUserRequestsPermissions();
  const showNotification =
    !isLoading && hasVisibleNewRequests(permissions, counters);

  return (
    <UserRequestsItem
      icon={SupervisedUserCircleIcon}
      to="user-requests"
      text={
        (
          <UserRequestsLabel>
            User Requests
            {showNotification && (
              <sup>
                <NewRequestsIcon />
              </sup>
            )}
          </UserRequestsLabel>
        ) as unknown as string
      }
    />
  );
};
